import { Link } from "react-router-dom";
import { ArrowRight, Camera, ShieldCheck, Coins, HardHat, Building2, Mail, UserCheck } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import PartOfWorkSafeAgent from "@/components/PartOfWorkSafeAgent";
import PricingTable from "@/components/PricingTable";

const STEPS = [
  { icon: Camera, n: "01", t: "Snap a photo of your reason" },
  { icon: ShieldCheck, n: "02", t: "Write a sentence — why you work safe" },
  { icon: UserCheck, n: "03", t: "Reviewed by a person on the safety team" },
  { icon: Coins, n: "04", t: "Earn a Safety Token at the company store" },
];

const FAQS = [
  {
    q: "Who can submit?",
    a: "Any employee with a company ID. One submission per shift is encouraged; there is no hard limit.",
  },
  {
    q: "What happens to my photo?",
    a: "It is reviewed by the safety team, then posted on the public Wall of Safety so the next shift can read it. Photos failing automated content review never appear publicly.",
  },
  {
    q: "How do I redeem my token?",
    a: "Show the SAFE-XXXX-XXXX code on your ticket (or screenshot it) at the company-store till. The cashier marks it redeemed in one tap. One token per submission, single use.",
  },
  {
    q: "Can I stay anonymous?",
    a: "Not currently. We collect name and employee ID so tokens can be issued and dispute-resolved. The Wall always shows the contributor's first name and last initial in production.",
  },
];

export default function About() {
  return (
    <div className="bg-white min-h-screen text-[#09090B]">
      <SiteHeader />

      {/* Hero */}
      <section className="border-b-2 border-[#09090B] bg-[#FFCC00]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-[1fr_auto] gap-10 items-end">
          <div>
            <div className="ws-label text-[#09090B]">/ About the program</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter mt-3 leading-[0.95]">
              WHY I WORK<br />SAFE — IN ONE PAGE.
            </h1>
            <p className="mt-6 max-w-xl text-[#09090B] text-lg leading-relaxed">
              <strong>worksafestory.com</strong> is a storytelling program from
              <strong> WorkSafeAgent LLC</strong>. Crews share the people, promises, and reasons behind
              every safe shift — and earn a Safety Token at the company store for doing it.
            </p>
          </div>
          <div className="border-2 border-[#09090B] bg-white p-6 ws-hard-shadow min-w-[200px]">
            <div className="ws-label text-[#FF5A00]">Goal</div>
            <div className="font-display font-black text-5xl tracking-tighter mt-1">0</div>
            <div className="ws-label text-[#09090B]">incidents · per shift</div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b-2 border-[#09090B]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
          <div className="ws-label text-[#FF5A00]">/ 01 — How it works</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-3">
            Four steps. One story. A token in your pocket.
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5 mt-10">
            {STEPS.map((s) => (
              <div key={s.n} className="border-2 border-[#09090B] bg-white p-6 ws-hard-shadow-sm" data-testid={`about-step-${s.n}`}>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 bg-[#09090B] text-white flex items-center justify-center">
                    <s.icon strokeWidth={2.5} className="w-5 h-5" />
                  </div>
                  <div className="font-display font-black text-4xl text-[#E4E4E7] tracking-tighter">{s.n}</div>
                </div>
                <div className="mt-5 font-display font-bold text-lg tracking-tight leading-snug">{s.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Token */}
      <section className="border-b-2 border-[#09090B] bg-[#F4F4F5]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-[auto_1fr] gap-10 items-start">
          <div className="border-2 border-[#09090B] bg-white p-8 ws-hard-shadow min-w-[260px]">
            <div className="ws-label text-[#FF5A00] flex items-center gap-2">
              <Coins strokeWidth={2.5} className="w-4 h-4" /> Safety Token
            </div>
            <div className="font-mono font-bold text-2xl tracking-widest mt-3">SAFE-XXXX-XXXX</div>
            <div className="ws-label text-[#52525B] mt-2">Single use · Company store</div>
          </div>
          <div>
            <div className="ws-label text-[#FF5A00]">/ 02 — How tokens redeem</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-3">
              One submission earns one token.
            </h2>
            <p className="mt-4 text-[#09090B] text-base leading-relaxed max-w-xl">
              Tokens appear on the confirmation screen the moment you submit. Show or screenshot the
              code, walk to the company store, and the cashier marks it redeemed. The system prevents
              double-spending — even if two cashiers try at the same second.
            </p>
            <Link
              to="/submit"
              data-testid="about-submit-cta"
              className="inline-flex items-center gap-2 mt-6 bg-[#FF5A00] hover:bg-[#E65100] text-white font-bold px-6 py-3 ws-label border-2 border-[#09090B] ws-hard-shadow-sm"
            >
              Share My Story <ArrowRight strokeWidth={2.5} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b-2 border-[#09090B]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
          <div className="ws-label text-[#FF5A00]">/ 03 — Common questions</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-3">
            What people ask at toolbox talks.
          </h2>
          <dl className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-8">
            {FAQS.map((f) => (
              <div key={f.q} data-testid={`about-faq-${f.q.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}>
                <dt className="font-display font-bold text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-[#52525B] leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Run by / Part of WorkSafeAgent */}
      <PartOfWorkSafeAgent />

      {/* Pricing */}
      <PricingTable />

      {/* Contact strip (compact — main brand details live in the strip above and the footer) */}
      <section className="bg-[#F4F4F5] border-b-2 border-[#09090B]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-8 grid md:grid-cols-[auto_1fr_auto] gap-6 items-center">
          <div className="w-12 h-12 bg-[#09090B] text-white flex items-center justify-center">
            <Building2 strokeWidth={2.5} className="w-6 h-6" />
          </div>
          <div>
            <div className="ws-label text-[#FF5A00]">/ Operated by</div>
            <div className="font-display font-black text-xl tracking-tighter mt-1">
              WorkSafeAgent LLC · Abilene, TX
            </div>
          </div>
          <a
            href="mailto:info@wsafeai.com"
            data-testid="about-contact-link"
            className="inline-flex items-center gap-2 bg-[#09090B] text-white ws-label px-5 py-3 border-2 border-[#09090B] hover:bg-[#FF5A00] transition-colors"
          >
            <Mail strokeWidth={2.5} className="w-4 h-4" /> info@wsafeai.com
          </a>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-[#FF5A00] text-white border-b-2 border-[#09090B]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 flex items-center justify-between flex-wrap gap-6">
          <div className="flex items-center gap-3">
            <HardHat strokeWidth={2.5} className="w-6 h-6 text-white" />
            <div className="ws-label text-white">Ready to share why you work safe?</div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/wall"
              data-testid="about-wall-link"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white font-bold px-5 py-2.5 ws-label hover:bg-white hover:text-[#09090B] transition-colors"
            >
              Read the wall
            </Link>
            <Link
              to="/submit"
              data-testid="about-share-link"
              className="inline-flex items-center gap-2 bg-white text-[#09090B] font-bold px-5 py-2.5 ws-label border-2 border-white hover:bg-[#FFCC00] transition-colors"
            >
              Share my story <ArrowRight strokeWidth={2.5} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
